/**
 * DeepSeek pricing — single source of truth.
 * All rates are per 1,000,000 tokens, in USD.
 * Update ONLY here — tables, calculator, "last verified" and JSON-LD
 * offers all read from this file.
 *
 * Fields marked _verify must be re-confirmed against the official page
 * before publishing changes: https://api-docs.deepseek.com/quick_start/pricing
 *
 * 2026-09-18 update (verified against the official rate card):
 * - DeepSeek-V4.1-Flash is GA and is now served by the `deepseek-flash`
 *   model name. The legacy `deepseek-v4-flash` and
 *   `deepseek-v4-flash-vision-exp` names still resolve but those models
 *   are retired; requests are served by V4.1-Flash at the Flash price.
 * - The rate card now has TWO time tiers. Peak hours are
 *   01:00–04:00 and 06:00–10:00 UTC, Monday–Friday. Off-peak rates are
 *   exactly half of peak rates. The peak surcharge is therefore live.
 * - Both models are more expensive than the previous flat rates; the old
 *   flat tiers moved to `history`.
 * - V4-Pro API service continues past 2026-09-14 with unchanged billing.
 *
 * The top-level per-model rates are the OFF-PEAK rates (the tier that
 * applies for most of the week). Peak rates live under `.peak`.
 */

export const DEEPSEEK_PRICING = {
  lastVerified: "2026-09-18",
  source: "https://api-docs.deepseek.com/quick_start/pricing",
  currency: "USD",
  perTokens: 1_000_000,

  models: {
    "v4-flash": {
      label: "DeepSeek-V4.1-Flash",
      apiName: "deepseek-flash",
      // Off-peak (default tier)
      inputCacheMiss: 0.15,
      inputCacheHit: 0.003,
      output: 0.6,
      peak: {
        inputCacheMiss: 0.3,
        inputCacheHit: 0.006,
        output: 1.2,
      },
    },
    "v4-pro": {
      label: "DeepSeek-V4-Pro",
      apiName: "deepseek-v4-pro",
      inputCacheMiss: 0.66,
      inputCacheHit: 0.022,
      output: 1.98,
      peak: {
        inputCacheMiss: 1.32,
        inputCacheHit: 0.044,
        output: 3.96,
      },
    },
  },

  // Superseded rates, kept for the "Price history" block on /pricing so
  // readers can reconcile old contracts and blog posts.
  history: [
    {
      model: "v4-pro" as const,
      effectiveUntil: "2026-05-31",
      note: "Previous V4-Pro standard rates. Superseded on 2026-05-31 by lower rates (originally introduced as a 75% promotional discount).",
      rates: { inputCacheMiss: 1.74, inputCacheHit: 0.0145, output: 3.48 },
    },
    {
      model: "v4-pro" as const,
      effectiveUntil: "2026-09-09",
      note: "Flat V4-Pro rates with no time tiers. Superseded when DeepSeek introduced peak / off-peak pricing alongside the V4.1-Flash GA release.",
      rates: { inputCacheMiss: 0.435, inputCacheHit: 0.003625, output: 0.87 },
    },
    {
      model: "v4-flash" as const,
      effectiveUntil: "2026-09-09",
      note: "Flat DeepSeek-V4-Flash rates with no time tiers. Superseded when DeepSeek introduced peak / off-peak pricing alongside the V4.1-Flash GA release.",
      rates: { inputCacheMiss: 0.14, inputCacheHit: 0.0028, output: 0.28 },
    },
  ],

  // Live since the V4.1-Flash GA rate card: peak rates are exactly 2× the
  // off-peak rates, i.e. a +100% surcharge inside the peak windows.
  peakSurcharge: {
    announced: true,
    applies: true,
    windowUTC: "01:00–04:00 & 06:00–10:00, Mon–Fri",
    windowUTC8: "09:00–12:00 & 14:00–18:00, Mon–Fri",
    surchargePct: 100 as number | null,
    startsOn: "2026-09-09" as string | null,
  },
} as const;

export type ModelKey = "v4-flash" | "v4-pro";

export function getRate(model: ModelKey) {
  const m = DEEPSEEK_PRICING.models[model];
  return {
    inputCacheMiss: m.inputCacheMiss,
    inputCacheHit: m.inputCacheHit,
    output: m.output,
  };
}

export function getPeakRate(model: ModelKey) {
  return DEEPSEEK_PRICING.models[model].peak;
}
